import React, { useEffect, useMemo } from "react";
import { Input } from "../components/ui/input";
import { TextArea } from "../components/ui/textarea";

/* ---------- Helpers ---------- */
const escapeHtml = (s = "") =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

const nl2br = (s = "") => s.replace(/\n/g, "<br>");
const linkify = (s = "") => {
  const url = /\b((?:https?:\/\/|www\.)[^\s<>"']+[^\s<>"'.,;:!?()\[\]{}])/gi;
  return s.replace(url, (match) => {
    const href = match.startsWith("http") ? match : `https://${match}`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
};

/* ---------- Component ---------- */
interface EmailTemplateEditorProps {
  email: {
    subject: string;
    header: string;
    subheader: string;
    text: string;
  };
  setEmail: React.Dispatch<
    React.SetStateAction<{
      subject: string;
      header: string;
      subheader: string;
      text: string;
    }>
  >;
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({ email, setEmail }) => {
  /* ---------- Default reset if empty ---------- */
  // useEffect(() => {
  //   setEmail((prev) => ({
  //     subject: prev.subject?.trim() || "Your feedback on {{courseTitle}}",
  //     header: prev.header?.trim() || "Hi {{name}},",
  //     subheader: prev.subheader?.trim() || "You have a new survey waiting!",
  //     text: prev.text?.trim() || "Your feedback helps improve our training programs.",
  //   }));
  // }, [email.subject, email.header, email.subheader, email.text]);

  /* ---------- Live preview ---------- */
  const previewHtml = useMemo(() => {
    const headerHtml = nl2br(linkify(escapeHtml(email.header)));
    const subheaderHtml = nl2br(linkify(escapeHtml(email.subheader)));
    const bodyHtml = nl2br(linkify(escapeHtml(email.text)));

    return `
  <body style="margin: 0; padding: 0; background-color: #fe8e02; font-family: 'Inter', Arial, sans-serif;">
    <table width="100%" height="100%" cellpadding="0" cellspacing="0" border="0" align="center" valign="top"
        style="width:100%; height:100%; background-color:#fe8e02;">
        <tr>
            <td align="center" valign="top" style="padding:40px 16px;">
                <img src="https://alpha.xuron.io/public/logo.png" width="360" alt="Xuron Insights"
                    style="display:block; margin:0 auto 24px; height:auto;" />
                <table width="100%" cellpadding="0" cellspacing="0" border="0"
                    style="max-width:600px; width:100%; background-color:#ffffff; border-radius:16px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.08);">
                    <tr>
                        <td style="padding:32px 32px;">
                            <div
                                style="width:100%; border-radius:12px; overflow:hidden; margin-bottom:32px; background:linear-gradient(135deg,#ff9a56 0%,#fe8e02 100%); text-align:center;">
                                <img src="https://alpha.xuron.io/public/banner.png" width="100%" alt="Survey Ready"
                                    style="display:block; object-fit:cover; width:100%; height:294px;" />
                            </div>
                            <h2 style="margin:0; color:#1d1d1f; font-weight:600; font-size:18px; text-align:center; margin-bottom:8px;">
                                ${headerHtml || "Hi {{name}},"}
                            </h2>
                            <h1 style="margin:0; color:#1d1d1f; font-weight:500; font-size:24px; text-align:center; margin-bottom:24px;">
                                ${subheaderHtml || "You have a new survey waiting!"}
                            </h1>
                            <div style="background-color:#fef7f0; border-radius:12px; padding:24px; text-align:center; margin-bottom:32px;">
                                <h3 style="margin:0; color:#1d1d1f; font-weight:600; font-size:16px; margin-bottom:8px; text-align:center;">
                                    {{courseTitle}}
                                </h3>
                                <div style="color:#86868b; font-weight:400; font-size:14px;">
                                    Course completed: {{courseDate}}
                                </div>
                            </div>
                            <p style="margin:0 0 16px; color:#4b5563; font-size:16px; line-height:1.5; text-align:center;">
                                ${bodyHtml || "Your feedback helps improve our training programs."}
                            </p>
                            <p style="margin:0 0 32px; color:#fe8e02; font-weight:600; font-size:16px; text-align:center;">
                                Complete it now and view your detailed learning insights!
                            </p>
                            <div style="text-align:center;">
                                <a href="{{surveyLink}}"
                                    style="background-color:#fe8e02; color:#fff; text-decoration:none; font-weight:700; font-size:18px; border-radius:28px; padding:16px 40px; display:inline-block;">
                                    Take Survey
                                </a>
                            </div>
                        </td>
                    </tr>
                </table>
                <div style="margin-top:24px; text-align:center; color:#ffffff;">
                    <p style="margin:0; font-size:14px; opacity:0.8;">Best regards,</p>
                    <p style="margin:0; font-size:16px; font-weight:600;">The Xuron Team</p>
                    <p style="margin:16px 0 0; font-size:12px; opacity:0.7;">© 2025 Xuron Inc</p>
                    <p style="margin:8px 0 0; font-size:12px; opacity:0.7;">
                        <a target="_blank" href="https://xuron.io/privacy-policy/" style="color:#fff; text-decoration:none;">Privacy</a>
                        |
                        <a target="_blank" href="https://xuron.io/terms-of-service/" style="color:#fff; text-decoration:none;">Terms</a>
                    </p>
                </div>
            </td>
        </tr>
    </table>
  </body>
  `;
  }, [email.header, email.subheader, email.text]);

  /* ---------- Render ---------- */
  return (
    <div className="flex gap-4">
      <div className="shadow flex h-fit w-[calc(98%-500px)] flex-col gap-5 p-6 rounded-2xl bg-white">
        <h1 className="text-lg font-semibold text-gray-900">Email Configuration</h1>

        <div className="flex flex-col gap-2">
          <h1 className="text-base font-semibold text-gray-900">Email Variables</h1>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {[
              { key: "{{name}}", desc: "Recipient's name" },
              { key: "{{courseTitle}}", desc: "Course title" },
              { key: "{{courseDate}}", desc: "Completion date" },
            ].map((variable) => (
              <div
                key={variable.key}
                className="flex flex-col rounded-xl border border-gray-200 bg-gray-50 p-3 hover:border-xuron-orange transition-all duration-200"
              >
                <span className="font-mono text-sm text-xuron-orange-dark">{variable.key}</span>
                <span className="text-xs text-gray-500">{variable.desc}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-gray-500 text-sm font-medium">Email Subject</p>
          <Input
            className="!bg-input-bg !rounded-xl"
            value={email.subject}
            onChange={(e) => setEmail((prev) => ({ ...prev, subject: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-gray-500 text-sm font-medium">Email Header</p>
          <Input
            className="!bg-input-bg !rounded-xl"
            value={email.header}
            onChange={(e) => setEmail((prev) => ({ ...prev, header: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-gray-500 text-sm font-medium">Email Subheader</p>
          <Input
            className="!bg-input-bg !rounded-xl"
            value={email.subheader}
            onChange={(e) => setEmail((prev) => ({ ...prev, subheader: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-gray-500 text-sm font-medium">Email Text</p>
          <TextArea
            className="!bg-input-bg !rounded-xl !min-h-[100px]"
            value={email.text}
            onChange={(e) => setEmail((prev) => ({ ...prev, text: e.target.value }))}
          />
        </div>
      </div>

      <div className="w-[500px] rounded-2xl overflow-hidden !h-fit relative">
        <div
          dangerouslySetInnerHTML={{ __html: previewHtml }}
          className="email-preview w-full origin-top-left"
          style={{
            transformOrigin: "top left",
            background: "#fff",
            borderRadius: "12px",
          }}
        />
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
